import { Icons } from '@commenda/kakashi';
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { Shimmer } from './ai-elements/shimmer';

interface ChainOfThoughtProps {
  /** True while the agent is still working (reasoning / running tools) before the answer begins. */
  isThinking: boolean;
  children: ReactNode;
}

const AUTO_COLLAPSE_DELAY_MS = 1000;

export function ChainOfThought({ isThinking, children }: ChainOfThoughtProps) {
  const [isOpen, setIsOpen] = useState(isThinking);
  const [duration, setDuration] = useState<number | undefined>(undefined);
  const startRef = useRef<number | null>(null);

  // Measure how long the agent spent thinking so the label can read "Thought for Ns".
  useEffect(() => {
    if (isThinking) {
      if (startRef.current === null) startRef.current = Date.now();
      setIsOpen(true);
    } else if (startRef.current !== null) {
      setDuration(
        Math.max(1, Math.round((Date.now() - startRef.current) / 1000))
      );
      startRef.current = null;
    }
  }, [isThinking]);

  // Collapse once thinking is done (keep the summary line, hide the trace by default).
  useEffect(() => {
    if (isThinking || !isOpen) return undefined;
    const timer = setTimeout(() => setIsOpen(false), AUTO_COLLAPSE_DELAY_MS);
    return () => clearTimeout(timer);
    // Deps limited to `isThinking` so the auto-collapse only arms when thinking finishes — not
    // again after a manual re-open.
  }, [isThinking]);

  const label = (() => {
    if (isThinking) return <Shimmer>Thinking</Shimmer>;
    if (duration === undefined) return 'Thought process';
    return `Thought for ${duration}s`;
  })();

  return (
    <div className="flex w-full flex-col gap-md">
      <button
        type="button"
        onClick={() => setIsOpen((prev) => !prev)}
        aria-expanded={isOpen}
        className="flex w-fit items-center gap-sm border-none bg-transparent p-0 txt-small-medium text-text-tertiary"
      >
        <span>{label}</span>
        {isOpen ? (
          <Icons.ChevronDown
            className="size-3 shrink-0 text-fg-quaternary"
            aria-hidden="true"
          />
        ) : (
          <Icons.ChevronRight
            className="size-3 shrink-0 text-fg-quaternary"
            aria-hidden="true"
          />
        )}
      </button>
      {isOpen && <div className="flex w-full flex-col gap-md">{children}</div>}
    </div>
  );
}
